<template>
    <b-card no-body v-show="isVisible">
        <template v-slot:header>
            <div class="d-flex justify-content-between align-items-center">
                <h4 class="mb-0">{{ title }}</h4>
            </div>
        </template>

        <b-card-body>
            <b-card-body v-if="isBusy">
                <b-alert show variant="info"><fa-icon icon="spinner" class="mr-2" spin />{{ busy.message }}</b-alert>
            </b-card-body>

            <b-card-body v-if="isReadOnly">
                <b-alert show variant="info">{{$t('email_templates.read_only_template')}}</b-alert>
            </b-card-body>


            <ValidationObserver ref="templateForm" tag="form" @submit.prevent="handleSubmit">
                <FormSlot :label="$t('email_templates.title')" :rules="{required: true}" v-slot="{errors}">
                    <input v-model="form.title" type="text" class="form-control" :placeholder="$t('email_templates.title')" :disabled="isDisabled"/>
                </FormSlot>

                <FormSlot :label="$t('email_templates.availability')" :rules="{required: true}" v-slot="{errors}">
                    <b-row>
                        <b-col cols="6" >
                            <b-select v-model="form.ownerUuid" :options="identifiersAsOptions" :disabled="isDisabled"></b-select>
                        </b-col>
                    </b-row>
                </FormSlot>

                <FormSlot :label="$t('email_templates.content')" :rules="{required: true}" v-slot="{errors}">
                    <vue-editor v-model="form.content" :disabled="isDisabled" :class="{'opacity-50': isDisabled}"/>
                </FormSlot>
            </ValidationObserver>
        </b-card-body>

        <b-card-footer>
            <b-row>
                <b-col cols="6">
                    <button-confirmation v-if="activeTemplateUuid && !isBusy && !isReadOnly">
                        <template v-slot:confirm="slotProps">
                            <b-button variant="danger" size="sm" class="mr-1" @click="remove">{{$t('email_templates.button_remove_confirm')}}</b-button>
                        </template>
                        <template v-slot:cancel="slotProps">
                            <b-button variant="outline-success" size="sm" class="mr-1" @click="slotProps.hideConfirm()">{{$t('email_templates.button_remove_cancel')}}</b-button>
                        </template>
                        <template v-slot:default="slotProps">
                            <b-button variant="danger" size="sm" class="mr-1" @click="slotProps.showConfirm()">{{$t('email_templates.button_remove')}}</b-button>
                        </template>
                    </button-confirmation>
                </b-col>
                <b-col cols="6" class="text-right">
                    <b-button variant="primary" size="sm" @click.prevent="handleSubmit" :disabled="isBusy" v-if="false === isReadOnly">
                        <fa-icon v-if="isBusy" icon="spinner" spin />
                        <span v-else>{{ isNew ? $t('_create') : $t('_save')}}</span>
                    </b-button>
                </b-col>
            </b-row>
        </b-card-footer>

    </b-card>
</template>

<script>
import store from "../store/store";
import {FormSlot} from '@dealer-group/dealerui';
import { VueEditor } from "vue2-editor";
import ButtonConfirmation from "../../../../components/Utilities/ButtonConfirmation";
import {DELETE_TEMPLATE, STORE_TEMPLATE, FETCH_SINGLE_TEMPLATE} from "../store/types";

const DEFAULT_FORM_VALUE = {
    active: true,
    content: '',
    title: '',
    ownerUuid: '',
    uuid: null,
    type: 'message'
};

export default {
    name: "TemplateModal",
    props: {
        value: Boolean,
    },

    components: {FormSlot, VueEditor, ButtonConfirmation},

    computed: {
        ...store.mapState(['identifiers']),
        ...store.mapFields(['activeTemplateUuid']),

        isNew() {
            return this.activeTemplateUuid === null;
        },
        isBusy() {
            return this.busy.active;
        },
        title() {
            if (this.isReadOnly) {
                return this.$t('email_templates.template_content');
            }
            return this.isNew ? this.$t('email_templates.new_template') : this.$t('email_templates.edit_template');
        },
        identifiersAsOptions() {
            return this.identifiers.map(item => ({
                text: this.resolveConnectionName(item.type, item.name),
                value: item.uuid
            }));
        },
        isVisible: {
            set(value) {
                this.$emit('input', value);
            },
            get() {
                return this.value;
            }
        },
        isReadOnly() {
            const readOnlyTemplates = this.identifiers
                .filter(identifier => identifier.readOnly)
                .map(identifier => identifier.uuid);
            return !!this.form.ownerUuid && readOnlyTemplates.includes(this.form.ownerUuid);
        },
        isDisabled() {
            return this.isReadOnly || this.isBusy;
        }
    },
    watch: {
        activeTemplateUuid: {
            immediate: true,
            handler() {
                this.reset();
                if (this.activeTemplateUuid) {
                    this.busy = {active: true, message: this.$t('email_templates.fetching_template_data')}
                    store.dispatch(FETCH_SINGLE_TEMPLATE, this.activeTemplateUuid)
                        .then(({data}) => this.form = data)
                        .finally(() => this.busy = {active: false, message: ''})
                }
            }
        },
        isVisible() {
            if (this.isVisible) {
                this.reset();
            }
        }
    },

    methods: {
        reset() {
            this.form = {...DEFAULT_FORM_VALUE};
            if (this.$refs.templateForm) {
                this.$refs.templateForm.reset();
            }
        },

        async handleSubmit() {
            const isValid = await this.$refs.templateForm.validate();
            if (!isValid) {
                return;
            }
            this.busy = {active: true, message: this.$t('_saving')};
            store.dispatch(STORE_TEMPLATE, {payload: this.form, isNew: this.isNew})
                .then(() => this.isVisible = !this.isNew)
                .finally(() => this.busy = {active: false, message: ''})
        },

        remove() {
            this.busy = {active: true, message: this.$t('_removing')};
            store.dispatch(DELETE_TEMPLATE, this.activeTemplateUuid)
                .then(() => this.isVisible = false)
                .finally(() => this.busy = {active: false, message: ''})
        },

        resolveConnectionName(type, name) {
          switch (type) {
            case 'user':
              return this.$t('email_templates.my_templates') + ` - ${name}`;
            case 'application':
              return this.$t('email_templates.application_templates') + ` - ${name}`;
            case 'application-type':
              return this.$t('email_templates.importer_templates') + ` - ${name}`;
          }
          return `[${type}] ${name}`;
        }
    },
    data: () => ({
        form: DEFAULT_FORM_VALUE,
        busy: {
            active: false,
            message: '',
        }
    })
}
</script>

<style scoped>

</style>